package songwritersnotebook.app.tutorial

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.LayoutCoordinates
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.zIndex

@Composable
fun TutorialOverlay(
    modifier: Modifier = Modifier,
    isGlobal: Boolean = true
) {
    val currentStep by TutorialManager.currentStep.collectAsState()
    val targetRect by TutorialManager.targetRect.collectAsState()
    val secondaryTargetRect by TutorialManager.secondaryTargetRect.collectAsState()
    val tutorialText by TutorialManager.tutorialText.collectAsState()
    val requiresClickTarget by TutorialManager.requiresClickTarget.collectAsState()
    val isCurrentStepLocal by TutorialManager.isCurrentStepLocal.collectAsState()

    if (currentStep == TutorialStep.NONE) return

    if (isGlobal && isCurrentStepLocal) return
    if (!isGlobal && !isCurrentStepLocal) return

    val density = LocalDensity.current
    var overlayCoords by remember { mutableStateOf<LayoutCoordinates?>(null) }

    val localTargetRect = remember(targetRect, overlayCoords) {
        if (targetRect != null && overlayCoords != null && overlayCoords!!.isAttached) {
            val coords = overlayCoords!!
            val topLeft = coords.windowToLocal(targetRect!!.topLeft)
            val bottomRight = coords.windowToLocal(targetRect!!.bottomRight)
            Rect(topLeft, bottomRight)
        } else {
            null
        }
    }

    val localSecondaryRect = remember(secondaryTargetRect, overlayCoords) {
        if (secondaryTargetRect != null && overlayCoords != null && overlayCoords!!.isAttached) {
            val coords = overlayCoords!!
            val topLeft = coords.windowToLocal(secondaryTargetRect!!.topLeft)
            val bottomRight = coords.windowToLocal(secondaryTargetRect!!.bottomRight)
            Rect(topLeft, bottomRight)
        } else {
            null
        }
    }

    BoxWithConstraints(
        modifier = modifier
            .fillMaxSize()
            .zIndex(100f)
            .onGloballyPositioned { overlayCoords = it }
    ) {
        val screenWidth = constraints.maxWidth.toFloat()
        val screenHeight = constraints.maxHeight.toFloat()
        val screenRect = Rect(0f, 0f, screenWidth, screenHeight)

        // Calculate dimmer rects
        val dimmerRects = remember(screenRect, localTargetRect, localSecondaryRect) {
            var rects = listOf(screenRect)
            
            if (localTargetRect != null) {
                rects = rects.flatMap { subtractRect(it, localTargetRect) }
            }
            if (localSecondaryRect != null) {
                rects = rects.flatMap { subtractRect(it, localSecondaryRect) }
            }
            rects
        }

        val dimColor = Color.Black.copy(alpha = 0.7f)

        // Draw Dimmer Boxes
        dimmerRects.forEach { rect ->
            Box(
                modifier = Modifier
                    .offset { IntOffset(rect.left.toInt(), rect.top.toInt()) }
                    .width(with(density) { rect.width.toDp() })
                    .height(with(density) { rect.height.toDp() })
                    .background(dimColor)
                    .clickable(enabled = false) {} // Block clicks
            )
        }

        // Tooltip Logic
        if (localTargetRect != null) {
            val rect = localTargetRect!!
            // Adjust threshold to be more balanced
            val showAbove = rect.center.y > screenHeight * 0.45f 

            val paddingValues = if (showAbove) {
                PaddingValues(
                    bottom = (with(density) { (screenHeight - rect.top).toDp() } + 8.dp).coerceAtLeast(16.dp),
                    start = 16.dp,
                    end = 16.dp
                )
            } else {
                PaddingValues(
                    top = (with(density) { rect.bottom.toDp() } + 8.dp).coerceAtLeast(16.dp),
                    start = 16.dp,
                    end = 16.dp
                )
            }

            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues),
                contentAlignment = if (showAbove) Alignment.BottomCenter else Alignment.TopCenter
            ) {
                TutorialTooltipCard(
                    text = tutorialText,
                    modifier = Modifier.widthIn(max = 300.dp)
                )
            }
        } else {
            // Center if no target
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(32.dp),
                contentAlignment = Alignment.Center
            ) {
                TutorialTooltipCard(
                    text = tutorialText,
                    modifier = Modifier.widthIn(max = 300.dp)
                )
            }
        }
    }
}

private fun subtractRect(source: Rect, hole: Rect): List<Rect> {
    if (!source.overlaps(hole)) return listOf(source)
    val intersection = source.intersect(hole)
    
    val res = mutableListOf<Rect>()
    
    // Top
    if (source.top < intersection.top) {
        res.add(Rect(source.left, source.top, source.right, intersection.top))
    }
    // Bottom
    if (source.bottom > intersection.bottom) {
        res.add(Rect(source.left, intersection.bottom, source.right, source.bottom))
    }
    // Left (middle section)
    if (source.left < intersection.left) {
        res.add(Rect(source.left, intersection.top, intersection.left, intersection.bottom))
    }
    // Right (middle section)
    if (source.right > intersection.right) {
        res.add(Rect(intersection.right, intersection.top, source.right, intersection.bottom))
    }
    return res
}

@Composable
fun TutorialTooltipCard(
    text: String,
    modifier: Modifier = Modifier
) {
    val stepIndex = TutorialManager.getCurrentStepIndex()
    val totalSteps = TutorialManager.getTotalSteps()

    Card(
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        elevation = CardDefaults.cardElevation(defaultElevation = 8.dp),
        modifier = modifier.clickable(enabled = false) {} // Catch clicks
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            // Header with Step Count and Skip
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
                modifier = Modifier.fillMaxWidth()
            ) {
                Text(
                    text = "Step ${stepIndex + 1} of $totalSteps",
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.primary
                )
                IconButton(
                    onClick = { TutorialManager.stopTutorial() },
                    modifier = Modifier.size(24.dp)
                ) {
                    Icon(
                        Icons.Default.Close,
                        contentDescription = "Skip Tutorial",
                        tint = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
            
            Spacer(modifier = Modifier.height(8.dp))

            Text(
                text = text,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurface
            )
            
            Spacer(modifier = Modifier.height(16.dp))

            // Navigation Buttons
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
                modifier = Modifier.fillMaxWidth()
            ) {
                TextButton(
                    onClick = { TutorialManager.previousStep() },
                    enabled = stepIndex > 0
                ) {
                    Text("Back")
                }
                
                Button(
                    onClick = { TutorialManager.triggerNext() }
                ) {
                    Text(if (stepIndex < totalSteps - 1) "Next" else "Finish")
                }
            }
        }
    }
}
